import { createServerFn } from '@tanstack/react-start';
import { and, asc, desc, eq, gt, isNull, lte, or, sql } from 'drizzle-orm';
import { nanoid } from 'nanoid';
import { z } from 'zod';
import {
  GOOGLE_ADS_BEGIN_CHECKOUT_CLAIM_LEASE_MS,
  getGoogleAdsBeginCheckoutRetryDelayMs,
} from '@/analytics/google-ads-begin-checkout-policy';
import {
  GOOGLE_ADS_FIRST_GENERATION_CLAIM_LEASE_MS,
  getGoogleAdsFirstGenerationRetryDelayMs,
  selectEarliestGoogleAdsGeneration,
} from '@/analytics/google-ads-first-generation-policy';
import { ensureGoogleAdsFirstGenerationConversionPending } from '@/analytics/google-ads-first-generation-server';
import {
  GOOGLE_ADS_PURCHASE_CLAIM_LEASE_MS,
  getGoogleAdsPurchaseNetValueAmount,
  getGoogleAdsPurchaseRetryDelayMs,
} from '@/analytics/google-ads-purchase-policy';
import {
  GOOGLE_ADS_SIGNUP_CLAIM_LEASE_MS,
  getGoogleAdsSignupRetryDelayMs,
  normalizeGoogleAdsEnhancedConversionEmail,
} from '@/analytics/google-ads-signup-policy';
import { ensureGoogleAdsSignupConversionPending } from '@/analytics/google-ads-signup-server';
import { getDb } from '@/db';
import {
  attributionTouch,
  generationHistory,
  googleAdsBeginCheckoutConversion,
  googleAdsBeginCheckoutConversionAttempt,
  googleAdsFirstGenerationConversion,
  googleAdsFirstGenerationConversionAttempt,
  googleAdsPurchaseConversion,
  googleAdsPurchaseConversionAttempt,
  googleAdsSignupConversion,
  googleAdsSignupConversionAttempt,
  videoGeneration,
} from '@/db/app.schema';
import { user } from '@/db/auth.schema';
import { authApiMiddleware } from '@/middlewares/auth-middleware';

const LEGACY_SIGNUP_BOOTSTRAP_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export const claimGoogleAdsSignupConversion = createServerFn({
  method: 'POST',
})
  .middleware([authApiMiddleware])
  .handler(async ({ context }) => {
    const db = getDb();
    const now = new Date();
    const [userRows, conversionRows, attributionRows] = await Promise.all([
      db
        .select({ email: user.email, createdAt: user.createdAt })
        .from(user)
        .where(eq(user.id, context.userId))
        .limit(1),
      db
        .select()
        .from(googleAdsSignupConversion)
        .where(eq(googleAdsSignupConversion.userId, context.userId))
        .limit(1),
      db
        .select({
          gclid: attributionTouch.gclid,
          gbraid: attributionTouch.gbraid,
          wbraid: attributionTouch.wbraid,
          landingPath: attributionTouch.landingPath,
          utmSource: attributionTouch.utmSource,
          utmMedium: attributionTouch.utmMedium,
          utmCampaign: attributionTouch.utmCampaign,
        })
        .from(attributionTouch)
        .where(eq(attributionTouch.userId, context.userId))
        .orderBy(desc(attributionTouch.capturedAt))
        .limit(1),
    ]);
    const userRow = userRows[0];
    if (!userRow) return notTrackable('missing');

    let conversion = conversionRows[0];
    if (!conversion) {
      const ageMs = now.getTime() - userRow.createdAt.getTime();
      if (ageMs < 0 || ageMs > LEGACY_SIGNUP_BOOTSTRAP_MAX_AGE_MS) {
        return notTrackable('missing');
      }

      await ensureGoogleAdsSignupConversionPending(
        context.userId,
        userRow.createdAt
      );
      const [inserted] = await db
        .select()
        .from(googleAdsSignupConversion)
        .where(eq(googleAdsSignupConversion.userId, context.userId))
        .limit(1);
      conversion = inserted;
    }

    if (!conversion) return notTrackable('missing');
    if (conversion.status === 'acknowledged') {
      return notTrackable('acknowledged');
    }
    if (
      conversion.status === 'expired' ||
      conversion.expiresAt.getTime() <= now.getTime()
    ) {
      await db
        .update(googleAdsSignupConversion)
        .set({ status: 'expired', updatedAt: now })
        .where(eq(googleAdsSignupConversion.userId, context.userId));
      return notTrackable('expired');
    }

    const leaseBefore = new Date(
      now.getTime() - GOOGLE_ADS_SIGNUP_CLAIM_LEASE_MS
    );
    const [claimed] = await db
      .update(googleAdsSignupConversion)
      .set({
        attemptCount: sql`${googleAdsSignupConversion.attemptCount} + 1`,
        lastAttemptAt: now,
        updatedAt: now,
      })
      .where(
        and(
          eq(googleAdsSignupConversion.userId, context.userId),
          eq(googleAdsSignupConversion.status, 'pending'),
          gt(googleAdsSignupConversion.expiresAt, now),
          or(
            isNull(googleAdsSignupConversion.lastAttemptAt),
            lte(googleAdsSignupConversion.lastAttemptAt, leaseBefore)
          )
        )
      )
      .returning({ attemptCount: googleAdsSignupConversion.attemptCount });

    if (!claimed) {
      const leaseRemainingMs = conversion.lastAttemptAt
        ? Math.max(
            GOOGLE_ADS_SIGNUP_CLAIM_LEASE_MS -
              (now.getTime() - conversion.lastAttemptAt.getTime()),
            1000
          )
        : GOOGLE_ADS_SIGNUP_CLAIM_LEASE_MS;
      return notTrackable('pending', leaseRemainingMs);
    }

    const attemptId = nanoid();
    const attribution = attributionRows[0];
    await db.insert(googleAdsSignupConversionAttempt).values({
      id: attemptId,
      userId: context.userId,
      attemptNumber: claimed.attemptCount,
      gclid: attribution?.gclid,
      gbraid: attribution?.gbraid,
      wbraid: attribution?.wbraid,
      landingPath: attribution?.landingPath,
      utmSource: attribution?.utmSource,
      utmMedium: attribution?.utmMedium,
      utmCampaign: attribution?.utmCampaign,
      attemptedAt: now,
    });

    return {
      shouldTrack: true as const,
      status: 'pending' as const,
      attemptId,
      transactionId: context.userId,
      enhancedConversionEmail: normalizeGoogleAdsEnhancedConversionEmail(
        userRow.email
      ),
      retryAfterMs: getGoogleAdsSignupRetryDelayMs(claimed.attemptCount),
    };
  });

const acknowledgementSchema = z.object({
  attemptId: z.string().min(1).max(64),
});

export const acknowledgeGoogleAdsSignupConversion = createServerFn({
  method: 'POST',
})
  .inputValidator(acknowledgementSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const db = getDb();
    const now = new Date();
    const [attempt] = await db
      .update(googleAdsSignupConversionAttempt)
      .set({ acknowledgedAt: now })
      .where(
        and(
          eq(googleAdsSignupConversionAttempt.id, data.attemptId),
          eq(googleAdsSignupConversionAttempt.userId, context.userId)
        )
      )
      .returning({ id: googleAdsSignupConversionAttempt.id });

    if (!attempt) return { acknowledged: false };

    await db
      .update(googleAdsSignupConversion)
      .set({
        status: 'acknowledged',
        acknowledgedAt: now,
        updatedAt: now,
      })
      .where(eq(googleAdsSignupConversion.userId, context.userId));

    return { acknowledged: true };
  });

const beginCheckoutClaimSchema = z.object({
  conversionId: z.string().min(1).max(64).optional(),
});

export const claimGoogleAdsBeginCheckoutConversion = createServerFn({
  method: 'POST',
})
  .inputValidator(beginCheckoutClaimSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const db = getDb();
    const now = new Date();

    await db
      .update(googleAdsBeginCheckoutConversion)
      .set({ status: 'expired', updatedAt: now })
      .where(
        and(
          eq(googleAdsBeginCheckoutConversion.userId, context.userId),
          eq(googleAdsBeginCheckoutConversion.status, 'pending'),
          lte(googleAdsBeginCheckoutConversion.expiresAt, now)
        )
      );

    const conversionQuery = data.conversionId
      ? db
          .select()
          .from(googleAdsBeginCheckoutConversion)
          .where(
            and(
              eq(googleAdsBeginCheckoutConversion.id, data.conversionId),
              eq(googleAdsBeginCheckoutConversion.userId, context.userId)
            )
          )
          .limit(1)
      : db
          .select()
          .from(googleAdsBeginCheckoutConversion)
          .where(
            and(
              eq(googleAdsBeginCheckoutConversion.userId, context.userId),
              eq(googleAdsBeginCheckoutConversion.status, 'pending'),
              gt(googleAdsBeginCheckoutConversion.expiresAt, now)
            )
          )
          .orderBy(asc(googleAdsBeginCheckoutConversion.createdAt))
          .limit(20);

    const [userRows, conversionRows, attributionRows] = await Promise.all([
      db
        .select({ email: user.email })
        .from(user)
        .where(eq(user.id, context.userId))
        .limit(1),
      conversionQuery,
      db
        .select({
          gclid: attributionTouch.gclid,
          gbraid: attributionTouch.gbraid,
          wbraid: attributionTouch.wbraid,
          landingPath: attributionTouch.landingPath,
          utmSource: attributionTouch.utmSource,
          utmMedium: attributionTouch.utmMedium,
          utmCampaign: attributionTouch.utmCampaign,
          utmTerm: attributionTouch.utmTerm,
          utmContent: attributionTouch.utmContent,
        })
        .from(attributionTouch)
        .where(eq(attributionTouch.userId, context.userId))
        .orderBy(desc(attributionTouch.capturedAt))
        .limit(1),
    ]);
    const userRow = userRows[0];
    if (!userRow || conversionRows.length === 0) {
      return beginCheckoutNotTrackable('missing');
    }
    const conversion = data.conversionId
      ? conversionRows[0]
      : conversionRows.find(
          (candidate) =>
            getBeginCheckoutRetryAfterMs(candidate, now.getTime()) === 0
        );
    if (!conversion) {
      const retryAfterMs = Math.min(
        ...conversionRows.map((candidate) =>
          getBeginCheckoutRetryAfterMs(candidate, now.getTime())
        )
      );
      return beginCheckoutNotTrackable('pending', Math.max(retryAfterMs, 1000));
    }
    if (conversion.status === 'acknowledged') {
      return beginCheckoutNotTrackable('acknowledged');
    }
    if (
      conversion.status === 'expired' ||
      conversion.expiresAt.getTime() <= now.getTime()
    ) {
      return beginCheckoutNotTrackable('expired');
    }

    if (conversion.lastAttemptAt) {
      const retryDelayMs = Math.max(
        GOOGLE_ADS_BEGIN_CHECKOUT_CLAIM_LEASE_MS,
        getGoogleAdsBeginCheckoutRetryDelayMs(conversion.attemptCount)
      );
      const retryAt = conversion.lastAttemptAt.getTime() + retryDelayMs;
      if (retryAt > now.getTime()) {
        return beginCheckoutNotTrackable(
          'pending',
          Math.max(retryAt - now.getTime(), 1000)
        );
      }
    }

    const leaseBefore = new Date(
      now.getTime() - GOOGLE_ADS_BEGIN_CHECKOUT_CLAIM_LEASE_MS
    );
    const [claimed] = await db
      .update(googleAdsBeginCheckoutConversion)
      .set({
        attemptCount: sql`${googleAdsBeginCheckoutConversion.attemptCount} + 1`,
        lastAttemptAt: now,
        updatedAt: now,
      })
      .where(
        and(
          eq(googleAdsBeginCheckoutConversion.id, conversion.id),
          eq(googleAdsBeginCheckoutConversion.userId, context.userId),
          eq(googleAdsBeginCheckoutConversion.status, 'pending'),
          eq(
            googleAdsBeginCheckoutConversion.attemptCount,
            conversion.attemptCount
          ),
          gt(googleAdsBeginCheckoutConversion.expiresAt, now),
          or(
            isNull(googleAdsBeginCheckoutConversion.lastAttemptAt),
            lte(googleAdsBeginCheckoutConversion.lastAttemptAt, leaseBefore)
          )
        )
      )
      .returning({
        attemptCount: googleAdsBeginCheckoutConversion.attemptCount,
      });

    if (!claimed) {
      return beginCheckoutNotTrackable(
        'pending',
        GOOGLE_ADS_BEGIN_CHECKOUT_CLAIM_LEASE_MS
      );
    }

    const attemptId = nanoid();
    const attribution = attributionRows[0];
    await db.insert(googleAdsBeginCheckoutConversionAttempt).values({
      id: attemptId,
      conversionId: conversion.id,
      userId: context.userId,
      attemptNumber: claimed.attemptCount,
      provider: conversion.provider,
      sessionId: conversion.sessionId,
      scene: conversion.scene,
      priceId: conversion.priceId,
      valueAmount: conversion.valueAmount,
      currency: conversion.currency,
      gclid: attribution?.gclid,
      gbraid: attribution?.gbraid,
      wbraid: attribution?.wbraid,
      landingPath: attribution?.landingPath,
      utmSource: attribution?.utmSource,
      utmMedium: attribution?.utmMedium,
      utmCampaign: attribution?.utmCampaign,
      utmTerm: attribution?.utmTerm,
      utmContent: attribution?.utmContent,
      attemptedAt: now,
    });

    return {
      shouldTrack: true as const,
      status: 'pending' as const,
      attemptId,
      transactionId: conversion.sessionId,
      enhancedConversionEmail: normalizeGoogleAdsEnhancedConversionEmail(
        userRow.email
      ),
      value: conversion.valueAmount / 100,
      currency: conversion.currency,
      retryAfterMs: getGoogleAdsBeginCheckoutRetryDelayMs(claimed.attemptCount),
    };
  });

export const acknowledgeGoogleAdsBeginCheckoutConversion = createServerFn({
  method: 'POST',
})
  .inputValidator(acknowledgementSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const db = getDb();
    const now = new Date();
    const [attempt] = await db
      .update(googleAdsBeginCheckoutConversionAttempt)
      .set({ acknowledgedAt: now })
      .where(
        and(
          eq(googleAdsBeginCheckoutConversionAttempt.id, data.attemptId),
          eq(googleAdsBeginCheckoutConversionAttempt.userId, context.userId)
        )
      )
      .returning({
        conversionId: googleAdsBeginCheckoutConversionAttempt.conversionId,
      });

    if (!attempt) return { acknowledged: false };

    await db
      .update(googleAdsBeginCheckoutConversion)
      .set({
        status: 'acknowledged',
        acknowledgedAt: now,
        updatedAt: now,
      })
      .where(
        and(
          eq(googleAdsBeginCheckoutConversion.id, attempt.conversionId),
          eq(googleAdsBeginCheckoutConversion.userId, context.userId)
        )
      );

    return { acknowledged: true };
  });

export const claimGoogleAdsPurchaseConversion = createServerFn({
  method: 'POST',
})
  .middleware([authApiMiddleware])
  .handler(async ({ context }) => {
    const db = getDb();
    const now = new Date();

    await db
      .update(googleAdsPurchaseConversion)
      .set({ status: 'expired', updatedAt: now })
      .where(
        and(
          eq(googleAdsPurchaseConversion.userId, context.userId),
          eq(googleAdsPurchaseConversion.status, 'pending'),
          lte(googleAdsPurchaseConversion.expiresAt, now)
        )
      );

    const [userRows, conversionRows, attributionRows] = await Promise.all([
      db
        .select({ email: user.email })
        .from(user)
        .where(eq(user.id, context.userId))
        .limit(1),
      db
        .select()
        .from(googleAdsPurchaseConversion)
        .where(
          and(
            eq(googleAdsPurchaseConversion.userId, context.userId),
            eq(googleAdsPurchaseConversion.status, 'pending'),
            gt(googleAdsPurchaseConversion.expiresAt, now)
          )
        )
        .orderBy(
          asc(
            sql`case when ${googleAdsPurchaseConversion.lastAttemptAt} is null then 0 else 1 end`
          ),
          asc(googleAdsPurchaseConversion.purchasedAt)
        )
        .limit(20),
      db
        .select({
          gclid: attributionTouch.gclid,
          gbraid: attributionTouch.gbraid,
          wbraid: attributionTouch.wbraid,
          landingPath: attributionTouch.landingPath,
          utmSource: attributionTouch.utmSource,
          utmMedium: attributionTouch.utmMedium,
          utmCampaign: attributionTouch.utmCampaign,
          utmTerm: attributionTouch.utmTerm,
          utmContent: attributionTouch.utmContent,
        })
        .from(attributionTouch)
        .where(eq(attributionTouch.userId, context.userId))
        .orderBy(desc(attributionTouch.capturedAt))
        .limit(1),
    ]);
    const userRow = userRows[0];
    if (!userRow || conversionRows.length === 0) {
      return purchaseNotTrackable('missing');
    }

    const conversion = conversionRows.find(
      (candidate) => getPurchaseRetryAfterMs(candidate, now.getTime()) === 0
    );
    if (!conversion) {
      const retryAfterMs = Math.min(
        ...conversionRows.map((candidate) =>
          getPurchaseRetryAfterMs(candidate, now.getTime())
        )
      );
      return purchaseNotTrackable('pending', Math.max(retryAfterMs, 1000));
    }

    const netValueAmount = getGoogleAdsPurchaseNetValueAmount(
      conversion.valueAmount,
      conversion.refundedAmount
    );
    if (netValueAmount <= 0) {
      await db
        .update(googleAdsPurchaseConversion)
        .set({
          status: 'refunded',
          adjustmentStatus: 'not_required',
          updatedAt: now,
        })
        .where(eq(googleAdsPurchaseConversion.id, conversion.id));
      return purchaseNotTrackable('refunded');
    }

    const leaseBefore = new Date(
      now.getTime() - GOOGLE_ADS_PURCHASE_CLAIM_LEASE_MS
    );
    const [claimed] = await db
      .update(googleAdsPurchaseConversion)
      .set({
        attemptCount: sql`${googleAdsPurchaseConversion.attemptCount} + 1`,
        lastAttemptAt: now,
        updatedAt: now,
      })
      .where(
        and(
          eq(googleAdsPurchaseConversion.id, conversion.id),
          eq(googleAdsPurchaseConversion.userId, context.userId),
          eq(googleAdsPurchaseConversion.status, 'pending'),
          eq(googleAdsPurchaseConversion.attemptCount, conversion.attemptCount),
          gt(googleAdsPurchaseConversion.expiresAt, now),
          or(
            isNull(googleAdsPurchaseConversion.lastAttemptAt),
            lte(googleAdsPurchaseConversion.lastAttemptAt, leaseBefore)
          )
        )
      )
      .returning({
        attemptCount: googleAdsPurchaseConversion.attemptCount,
      });
    if (!claimed) {
      return purchaseNotTrackable(
        'pending',
        GOOGLE_ADS_PURCHASE_CLAIM_LEASE_MS
      );
    }

    const attemptId = nanoid();
    const attribution = attributionRows[0];
    await db.insert(googleAdsPurchaseConversionAttempt).values({
      id: attemptId,
      conversionId: conversion.id,
      userId: context.userId,
      attemptNumber: claimed.attemptCount,
      provider: conversion.provider,
      transactionId: conversion.transactionId,
      scene: conversion.scene,
      priceId: conversion.priceId,
      valueAmount: netValueAmount,
      currency: conversion.currency,
      newCustomer: conversion.newCustomer,
      gclid: attribution?.gclid,
      gbraid: attribution?.gbraid,
      wbraid: attribution?.wbraid,
      landingPath: attribution?.landingPath,
      utmSource: attribution?.utmSource,
      utmMedium: attribution?.utmMedium,
      utmCampaign: attribution?.utmCampaign,
      utmTerm: attribution?.utmTerm,
      utmContent: attribution?.utmContent,
      attemptedAt: now,
    });

    return {
      shouldTrack: true as const,
      status: 'pending' as const,
      attemptId,
      transactionId: conversion.transactionId,
      enhancedConversionEmail: normalizeGoogleAdsEnhancedConversionEmail(
        userRow.email
      ),
      value: netValueAmount / 100,
      currency: conversion.currency,
      newCustomer: conversion.newCustomer,
      retryAfterMs: getGoogleAdsPurchaseRetryDelayMs(claimed.attemptCount),
    };
  });

export const acknowledgeGoogleAdsPurchaseConversion = createServerFn({
  method: 'POST',
})
  .inputValidator(acknowledgementSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const db = getDb();
    const now = new Date();
    const [attempt] = await db
      .update(googleAdsPurchaseConversionAttempt)
      .set({ acknowledgedAt: now })
      .where(
        and(
          eq(googleAdsPurchaseConversionAttempt.id, data.attemptId),
          eq(googleAdsPurchaseConversionAttempt.userId, context.userId)
        )
      )
      .returning({
        conversionId: googleAdsPurchaseConversionAttempt.conversionId,
      });
    if (!attempt) return { acknowledged: false };

    const [acknowledged] = await db
      .update(googleAdsPurchaseConversion)
      .set({
        status: 'acknowledged',
        acknowledgedAt: now,
        updatedAt: now,
      })
      .where(
        and(
          eq(googleAdsPurchaseConversion.id, attempt.conversionId),
          eq(googleAdsPurchaseConversion.userId, context.userId),
          eq(googleAdsPurchaseConversion.status, 'pending')
        )
      )
      .returning({ id: googleAdsPurchaseConversion.id });

    if (!acknowledged) {
      await db
        .update(googleAdsPurchaseConversion)
        .set({
          status: 'acknowledged',
          acknowledgedAt: now,
          adjustmentStatus: 'pending',
          updatedAt: now,
        })
        .where(
          and(
            eq(googleAdsPurchaseConversion.id, attempt.conversionId),
            eq(googleAdsPurchaseConversion.userId, context.userId),
            eq(googleAdsPurchaseConversion.status, 'refunded')
          )
        );
    }

    return { acknowledged: true };
  });

export const claimGoogleAdsFirstGenerationConversion = createServerFn({
  method: 'POST',
})
  .middleware([authApiMiddleware])
  .handler(async ({ context }) => {
    const db = getDb();
    const now = new Date();
    const [userRows, conversionRows, attributionRows] = await Promise.all([
      db
        .select({ email: user.email })
        .from(user)
        .where(eq(user.id, context.userId))
        .limit(1),
      db
        .select()
        .from(googleAdsFirstGenerationConversion)
        .where(eq(googleAdsFirstGenerationConversion.userId, context.userId))
        .limit(1),
      db
        .select({
          gclid: attributionTouch.gclid,
          gbraid: attributionTouch.gbraid,
          wbraid: attributionTouch.wbraid,
          landingPath: attributionTouch.landingPath,
          utmSource: attributionTouch.utmSource,
          utmMedium: attributionTouch.utmMedium,
          utmCampaign: attributionTouch.utmCampaign,
          utmTerm: attributionTouch.utmTerm,
          utmContent: attributionTouch.utmContent,
        })
        .from(attributionTouch)
        .where(eq(attributionTouch.userId, context.userId))
        .orderBy(desc(attributionTouch.capturedAt))
        .limit(1),
    ]);
    const userRow = userRows[0];
    if (!userRow) return firstGenerationNotTrackable('missing');

    let conversion = conversionRows[0];
    if (!conversion) {
      const earliestGeneration = await findEarliestCompletedGeneration(
        context.userId
      );
      if (!earliestGeneration) {
        return firstGenerationNotTrackable('missing');
      }

      await ensureGoogleAdsFirstGenerationConversionPending({
        userId: context.userId,
        generationId: earliestGeneration.generationId,
        generationType: earliestGeneration.generationType,
        completedAt: earliestGeneration.completedAt,
      });
      const [inserted] = await db
        .select()
        .from(googleAdsFirstGenerationConversion)
        .where(eq(googleAdsFirstGenerationConversion.userId, context.userId))
        .limit(1);
      conversion = inserted;
    }

    if (!conversion) return firstGenerationNotTrackable('missing');
    if (conversion.status === 'acknowledged') {
      return firstGenerationNotTrackable('acknowledged');
    }
    if (conversion.status === 'suppressed') {
      return firstGenerationNotTrackable('suppressed');
    }
    if (
      conversion.status === 'expired' ||
      conversion.expiresAt.getTime() <= now.getTime()
    ) {
      await db
        .update(googleAdsFirstGenerationConversion)
        .set({ status: 'expired', updatedAt: now })
        .where(eq(googleAdsFirstGenerationConversion.userId, context.userId));
      return firstGenerationNotTrackable('expired');
    }

    if (conversion.lastAttemptAt) {
      const retryDelayMs = Math.max(
        GOOGLE_ADS_FIRST_GENERATION_CLAIM_LEASE_MS,
        getGoogleAdsFirstGenerationRetryDelayMs(conversion.attemptCount)
      );
      const retryAt = conversion.lastAttemptAt.getTime() + retryDelayMs;
      if (retryAt > now.getTime()) {
        return firstGenerationNotTrackable(
          'pending',
          Math.max(retryAt - now.getTime(), 1000)
        );
      }
    }

    const leaseBefore = new Date(
      now.getTime() - GOOGLE_ADS_FIRST_GENERATION_CLAIM_LEASE_MS
    );
    const [claimed] = await db
      .update(googleAdsFirstGenerationConversion)
      .set({
        attemptCount: sql`${googleAdsFirstGenerationConversion.attemptCount} + 1`,
        lastAttemptAt: now,
        updatedAt: now,
      })
      .where(
        and(
          eq(googleAdsFirstGenerationConversion.userId, context.userId),
          eq(googleAdsFirstGenerationConversion.status, 'pending'),
          eq(
            googleAdsFirstGenerationConversion.attemptCount,
            conversion.attemptCount
          ),
          gt(googleAdsFirstGenerationConversion.expiresAt, now),
          or(
            isNull(googleAdsFirstGenerationConversion.lastAttemptAt),
            lte(googleAdsFirstGenerationConversion.lastAttemptAt, leaseBefore)
          )
        )
      )
      .returning({
        attemptCount: googleAdsFirstGenerationConversion.attemptCount,
      });

    if (!claimed) {
      return firstGenerationNotTrackable(
        'pending',
        GOOGLE_ADS_FIRST_GENERATION_CLAIM_LEASE_MS
      );
    }

    const attemptId = nanoid();
    const attribution = attributionRows[0];
    await db.insert(googleAdsFirstGenerationConversionAttempt).values({
      id: attemptId,
      userId: context.userId,
      attemptNumber: claimed.attemptCount,
      generationId: conversion.generationId,
      generationType: conversion.generationType,
      gclid: attribution?.gclid,
      gbraid: attribution?.gbraid,
      wbraid: attribution?.wbraid,
      landingPath: attribution?.landingPath,
      utmSource: attribution?.utmSource,
      utmMedium: attribution?.utmMedium,
      utmCampaign: attribution?.utmCampaign,
      utmTerm: attribution?.utmTerm,
      utmContent: attribution?.utmContent,
      attemptedAt: now,
    });

    return {
      shouldTrack: true as const,
      status: 'pending' as const,
      attemptId,
      transactionId: context.userId,
      enhancedConversionEmail: normalizeGoogleAdsEnhancedConversionEmail(
        userRow.email
      ),
      retryAfterMs: getGoogleAdsFirstGenerationRetryDelayMs(
        claimed.attemptCount
      ),
    };
  });

export const acknowledgeGoogleAdsFirstGenerationConversion = createServerFn({
  method: 'POST',
})
  .inputValidator(acknowledgementSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const db = getDb();
    const now = new Date();
    const [attempt] = await db
      .update(googleAdsFirstGenerationConversionAttempt)
      .set({ acknowledgedAt: now })
      .where(
        and(
          eq(googleAdsFirstGenerationConversionAttempt.id, data.attemptId),
          eq(googleAdsFirstGenerationConversionAttempt.userId, context.userId)
        )
      )
      .returning({ id: googleAdsFirstGenerationConversionAttempt.id });

    if (!attempt) return { acknowledged: false };

    await db
      .update(googleAdsFirstGenerationConversion)
      .set({
        status: 'acknowledged',
        acknowledgedAt: now,
        updatedAt: now,
      })
      .where(eq(googleAdsFirstGenerationConversion.userId, context.userId));

    return { acknowledged: true };
  });

async function findEarliestCompletedGeneration(userId: string) {
  const db = getDb();
  const [imageRows, videoRows] = await Promise.all([
    db
      .select({
        generationId: generationHistory.id,
        completedAt: generationHistory.completedAt,
        createdAt: generationHistory.createdAt,
      })
      .from(generationHistory)
      .where(
        and(
          eq(generationHistory.userId, userId),
          eq(generationHistory.status, 'completed')
        )
      )
      .orderBy(
        asc(
          sql`coalesce(${generationHistory.completedAt}, ${generationHistory.createdAt})`
        ),
        asc(generationHistory.id)
      )
      .limit(1),
    db
      .select({
        generationId: videoGeneration.id,
        completedAt: videoGeneration.completedAt,
        createdAt: videoGeneration.createdAt,
      })
      .from(videoGeneration)
      .where(
        and(
          eq(videoGeneration.userId, userId),
          eq(videoGeneration.status, 'completed')
        )
      )
      .orderBy(
        asc(
          sql`coalesce(${videoGeneration.completedAt}, ${videoGeneration.createdAt})`
        ),
        asc(videoGeneration.id)
      )
      .limit(1),
  ]);

  return selectEarliestGoogleAdsGeneration([
    ...imageRows.map((row) => ({
      ...row,
      generationType: 'image' as const,
    })),
    ...videoRows.map((row) => ({
      ...row,
      generationType: 'video' as const,
    })),
  ]);
}

function notTrackable(
  status: 'acknowledged' | 'expired' | 'missing' | 'pending',
  retryAfterMs: number | null = null
) {
  return {
    shouldTrack: false as const,
    status,
    attemptId: null,
    transactionId: null,
    enhancedConversionEmail: null,
    retryAfterMs,
  };
}

function firstGenerationNotTrackable(
  status: 'acknowledged' | 'expired' | 'missing' | 'pending' | 'suppressed',
  retryAfterMs: number | null = null
) {
  return {
    shouldTrack: false as const,
    status,
    attemptId: null,
    transactionId: null,
    enhancedConversionEmail: null,
    retryAfterMs,
  };
}

function beginCheckoutNotTrackable(
  status: 'acknowledged' | 'expired' | 'missing' | 'pending',
  retryAfterMs: number | null = null
) {
  return {
    shouldTrack: false as const,
    status,
    attemptId: null,
    transactionId: null,
    enhancedConversionEmail: null,
    value: null,
    currency: null,
    retryAfterMs,
  };
}

function purchaseNotTrackable(
  status:
    | 'acknowledged'
    | 'expired'
    | 'missing'
    | 'pending'
    | 'refunded'
    | 'suppressed',
  retryAfterMs: number | null = null
) {
  return {
    shouldTrack: false as const,
    status,
    attemptId: null,
    transactionId: null,
    enhancedConversionEmail: null,
    value: null,
    currency: null,
    newCustomer: null,
    retryAfterMs,
  };
}

function getBeginCheckoutRetryAfterMs(
  conversion: {
    attemptCount: number;
    lastAttemptAt: Date | null;
  },
  now: number
) {
  if (!conversion.lastAttemptAt) return 0;
  const retryDelayMs = Math.max(
    GOOGLE_ADS_BEGIN_CHECKOUT_CLAIM_LEASE_MS,
    getGoogleAdsBeginCheckoutRetryDelayMs(conversion.attemptCount)
  );
  return Math.max(conversion.lastAttemptAt.getTime() + retryDelayMs - now, 0);
}

function getPurchaseRetryAfterMs(
  conversion: {
    attemptCount: number;
    lastAttemptAt: Date | null;
  },
  now: number
) {
  if (!conversion.lastAttemptAt) return 0;
  const retryDelayMs = Math.max(
    GOOGLE_ADS_PURCHASE_CLAIM_LEASE_MS,
    getGoogleAdsPurchaseRetryDelayMs(conversion.attemptCount)
  );
  return Math.max(conversion.lastAttemptAt.getTime() + retryDelayMs - now, 0);
}
